// Copyright (c) Docugami, Inc. All rights reserved.

import { CachedDownloadStatus } from "hooks/downloader";
import { StorageAccessor, StorageKey } from "./StorageAccessor";

export function getDebugLoadDelay(): number {
    return new StorageAccessor().getNumber(StorageKey.DebugLoadDelay, 0);
}

export function getLocalStorageAssistedAuthoringContextModeValue(): boolean {
    return new StorageAccessor().getBoolean(StorageKey.AAContextualMode, false);
}

export function setLocalStorageAssistedAuthoringContextModeValue(value: boolean) {
    new StorageAccessor().setBoolean(StorageKey.AAContextualMode, value);
}

export function getLocalStorageAssistedAuthoringCardData(): any {
    return new StorageAccessor().getObject(StorageKey.AACardData);
}

export function setLocalStorageAssistedAuthoringCardData(value: any) {
    new StorageAccessor().setObject(StorageKey.AACardData, value);
}

export function setLocalStorageOrganizationId(orgId: string) {
    new StorageAccessor().setString(StorageKey.LastOrganizationId, orgId);
}

export function getLocalStorageOrganizationId(): string | null {
    return new StorageAccessor().getString(StorageKey.LastOrganizationId);
}

export function getLocalStorageLongDownloadsInfo(): CachedDownloadStatus | null {
    return new StorageAccessor().getObject<CachedDownloadStatus>(StorageKey.ActiveLongDownloads);
}

export function setLocalStorageLongDownloadsInfo(value: CachedDownloadStatus) {
    new StorageAccessor().setObject(StorageKey.ActiveLongDownloads, value);
}

export function setSelfServiceAccessCode(code: string) {
    new StorageAccessor().setString(StorageKey.SelfServiceAccessCode, code);
}

export function getSelfServiceAccessCode(): string | null {
    return new StorageAccessor().getString(StorageKey.SelfServiceAccessCode);
}

export function setLastActiveUpdateTime(date: Date) {
    new StorageAccessor().setDate(StorageKey.LastActiveUpdateTime, date);
}

export function getLastActiveUpdateTime(): Date | null {
    return new StorageAccessor().getDate(StorageKey.LastActiveUpdateTime);
}

export function setLastActiveTime(date: Date) {
    new StorageAccessor().setDate(StorageKey.LastActiveTime, date);
}

export function getLastActiveTime(): Date | null {
    return new StorageAccessor().getDate(StorageKey.LastActiveTime);
}

export function setNumberOfChunkSuggestionsForDemoMode(numSuggestions: number) {
    new StorageAccessor().setNumber(StorageKey.NumberOfSuggestedChunksInDemoMode, numSuggestions);
}

export function getNumberOfChunkSuggestionsForDemoMode(): number {
    return new StorageAccessor().getNumber(StorageKey.NumberOfSuggestedChunksInDemoMode, 20);
}

const createFirstOneClickWorkPathViewingKey = (workPathId: string): string => `${StorageKey.IsFirstOneClickWorkPathViewing}_${workPathId}`

export function setShowFirstOneClickWorkPathViewingUx(workPathId: string, show: boolean) {
    if (!show) {
        return new StorageAccessor().removeKey(createFirstOneClickWorkPathViewingKey(workPathId));
    }

    return new StorageAccessor().setBoolean(createFirstOneClickWorkPathViewingKey(workPathId), show);
}

export function getShowFirstOneClickWorkPathViewingUx(workPathId: string): boolean {
    return new StorageAccessor().getBoolean(createFirstOneClickWorkPathViewingKey(workPathId), false);
}

export function setFirstArrivalComplete(orgId: string) {
    new StorageAccessor(window.localStorage, `docugami.${orgId}.`).setBoolean(StorageKey.FirstArrival, false);
}

export function getIsFirstArrival(orgId: string): boolean | undefined {
    return new StorageAccessor(window.localStorage, `docugami.${orgId}.`).getBoolean(StorageKey.FirstArrival);
}

export function getDefaultProjectId(orgId: string): string | null {
    return new StorageAccessor(window.localStorage, `docugami.${orgId}.`).getString(StorageKey.DefaultProjectId);
}

export function setDefaultProjectId(orgId: string, projectId?: string | null): void {
    const accessor = new StorageAccessor(window.localStorage, `docugami.${orgId}.`)
    if (!!projectId) {
        accessor.setString(StorageKey.DefaultProjectId, projectId);
    }
    else {
        accessor.removeKey(StorageKey.DefaultProjectId);
    }
}

const createPreviewColorKey = (reportId: string, entryId: string) => `${StorageKey.ReportEntryPreviewColor}_${reportId}_${entryId}`;

export function getReportEntryPreviewColor(reportId: string, entryId: string): string | null {
    return new StorageAccessor().getString(createPreviewColorKey(reportId, entryId));
}

export function setReportEntryPreviewColor(reportId: string, entryId: string, color?: string): void {
    const key = createPreviewColorKey(reportId, entryId);
    if (!!color) {
        new StorageAccessor().setString(key, color);
    }
    else {
        new StorageAccessor().removeKey(key);
    }
}

export function getReportsChatWidth(): number {
    return new StorageAccessor().getNumber(StorageKey.ReportsChatWidth, 22);
}

export function setReportsChatWidth(width: number): void {
    new StorageAccessor().setNumber(StorageKey.ReportsChatWidth, width);
}

export function getReportsChatCollapsed(): boolean {
    return new StorageAccessor().getBoolean(StorageKey.ReportsChatCollapsed, false);
}

export function setReportsChatCollapsed(collapsed: boolean): void {
    new StorageAccessor().setBoolean(StorageKey.ReportsChatCollapsed, collapsed);
}
